import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { createGlobalStyle, StyleSheetManager } from 'styled-components';

// DRUK IDZIE Z UKRYTEJ RAMKI, NIE ZE STRONY APLIKACJI. Ramka ma własny, czysty dokument: tylko
// to, co ma trafić na papier, i reguła `@page`, bez menu, bez `#root` i bez stylów panelu.
// `#root` jest kontenerem przewijania, więc przeglądarka drukowała z niego tylko pierwszą stronę,
// a treść odkrywana regułą CSS na czas druku budowała podgląd z całej aplikacji (okno druku
// w Edge rysowało się przesunięte i nie zasłaniało strony). Style trafiają do ramki przez
// `StyleSheetManager target` (styled-components wstrzykuje je inaczej do głowy głównego
// dokumentu, której ramka nie widzi). Zmienne CSS motywu w ramce NIE istnieją: komponenty
// renderowane do druku podają kolory wprost.
//
// Wydzielone z pages/EquipmentLabels.js przy U.29 (plan dnia w kalendarzu drukuje tak samo).

const FrameStyles = createGlobalStyle`
  @page { ${(p) => p.$page} }
  html, body { margin: 0; padding: 0; background: #fff; }
`;

const frameDoc = (title) => {
  const safe = String(title).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  // Tytuł dokumentu w ramce podpowiada przeglądarce nazwę pliku przy „Zapisz jako PDF”.
  return `<!DOCTYPE html><html lang="pl"><head><meta charset="utf-8"><title>${safe}</title></head><body></body></html>`;
};

/**
 * Ukryta ramka z treścią do druku. Zerowy rozmiar, ale NIE display:none ani visibility:hidden:
 * część przeglądarek drukuje wtedy pustą stronę.
 *
 * `page` to treść reguły `@page` (np. `size: A4; margin: 14mm;`), `rootId` to id pojemnika
 * z treścią w dokumencie ramki, `dataCy` kotwica testów na samej ramce. `documentTitle` zmienia
 * tytuł dokumentu BEZ przeładowania ramki (zmiana `title` buduje dokument od nowa), np. gdy
 * nazwa pliku ma nieść datę.
 */
export default function PrintFrame({ frameRef, title, documentTitle, page, rootId, dataCy, children }) {
  const [doc, setDoc] = useState(null);
  const html = useMemo(() => frameDoc(title), [title]);
  useEffect(() => {
    if (doc && documentTitle) doc.title = documentTitle;
  }, [doc, documentTitle]);
  return (
    <>
      <iframe
        ref={frameRef}
        title={title}
        srcDoc={html}
        aria-hidden="true"
        tabIndex={-1}
        onLoad={(e) => setDoc(e.currentTarget.contentDocument)}
        style={{ position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 0 }}
        data-cy={dataCy}
      />
      {doc && doc.body && ReactDOM.createPortal(
        <StyleSheetManager target={doc.head}>
          <>
            <FrameStyles $page={page} />
            <div id={rootId}>{children}</div>
          </>
        </StyleSheetManager>,
        doc.body,
      )}
    </>
  );
}

/**
 * Drukuje dokument ramki. Czeka na zdekodowanie obrazków, żeby podgląd druku nie złapał pustych
 * pól. Zwraca `false` i NIE drukuje, gdy ramka nie jest jeszcze gotowa. Sam `contentDocument`
 * tego nie rozstrzyga: przed wczytaniem `srcDoc` ramka ma już pusty dokument startowy, a między
 * jego wczytaniem a wyrenderowaniem treści `<body>` jest puste, więc wydrukowałaby się biała kartka.
 */
export async function printFrame(frame) {
  const win = frame && frame.contentWindow;
  const doc = frame && frame.contentDocument;
  if (!win || !doc || !doc.body || !doc.body.firstElementChild) return false;
  await Promise.all([...doc.images].map((img) => (img.decode ? img.decode().catch(() => {}) : null)));
  win.focus();
  win.print();
  return true;
}
